import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight, MessageSquare, CalendarCheck, ShieldCheck, Ruler, MapPin, LayoutGrid, Sun,
  Layers, Sparkles, GraduationCap, Briefcase, BadgeCheck, Wallet, X,
} from "lucide-react";
import { initLenis } from "../lib/smooth";
import { getSiteSettings, digitsOnly } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/ContactFooter";
import { LeadModal } from "../components/LeadForm";
import { StickyMobileBar, WhatsAppFloat } from "../components/StickyElements";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionHeading } from "../components/Reveal";
import { IMAGES, AGENT_REG, FLOOR_PLANS, WA_MESSAGE } from "../data/site";

const SMART = FLOOR_PLANS.filter((p) => p.category === "3 BHK Smart");
const COMFORT = FLOOR_PLANS.filter((p) => p.category === "3 BHK Comfort");
const LUXURY = FLOOR_PLANS.filter((p) => p.category === "3 BHK Luxury");

const WHY_3BHK = [
  { icon: Ruler, title: "Space Requirements", desc: "A third bedroom flexes between children's room, study, guest room or home office." },
  { icon: MapPin, title: "Location & Connectivity", desc: "Metro access and real commute times shape daily life more than any brochure claim." },
  { icon: LayoutGrid, title: "Floor-Plan Efficiency", desc: "Compare usable carpet area, not just super built-up figures." },
  { icon: Sun, title: "Natural Light & Ventilation", desc: "Open-sided 3 BHK homes stay brighter and cooler through the day." },
  { icon: Layers, title: "Balconies & Open Spaces", desc: "Balconies and sky terraces extend compact plans outdoors." },
  { icon: Sparkles, title: "Amenities", desc: "Prioritise the facilities your family will use every week." },
  { icon: GraduationCap, title: "Schools & Healthcare", desc: "Reputed schools and hospitals within easy reach simplify family life." },
  { icon: Briefcase, title: "Employment Hubs", desc: "Proximity to the Action Area III IT corridor shortens commutes." },
  { icon: BadgeCheck, title: "RERA Verification", desc: "Verify the project and agent registration on the WBRERA website before booking." },
  { icon: Wallet, title: "Budget", desc: "Compare complete pricing including applicable charges, not just base rates." },
];

const CATEGORY_INFO = [
  {
    key: "smart",
    title: "3 BHK Smart",
    plans: SMART,
    desc: "The Smart family is the most efficient 3 BHK at the project — 2-toilet layouts of approximately 698–703 sq ft carpet area, with balcony, BUA and SBUA varying by floor. Available across Towers 1A, 1B, 2A, 2B and 2C.",
  },
  {
    key: "comfort",
    title: "3 BHK Comfort",
    plans: COMFORT,
    desc: "Comfort layouts step up to roughly 736–783 sq ft carpet area with more generous rooms — 2-toilet and 3-toilet variants depending on tower and type, with odd/even floor variation in balcony, BUA and SBUA.",
  },
  {
    key: "luxury",
    title: "3 BHK Luxury",
    plans: LUXURY,
    desc: "Luxury layouts are the largest residences — approximately 823–943 sq ft carpet area with 3 toilets and expansive double-height sky terraces. Twenty variants span all five towers.",
  },
];

const THREE_BHK_FAQS = [
  {
    q: "Are 3 BHK flats available in New Town Kolkata?",
    a: "Yes. New Town offers a wide range of 3 BHK homes, including near the Action Area III vicinity — Vinayak 21 Acres at Hatishala, adjacent to Newtown Action Area III, offers 3 BHK homes in Smart, Comfort and Luxury configurations.",
  },
  {
    q: "What types of 3 BHK layouts are available at Vinayak 21 Acres?",
    a: "Three families: Smart (2-toilet, ~698–703 sq ft carpet), Comfort (~736–783 sq ft carpet, 2 or 3 toilets) and Luxury (3-toilet, ~823–943 sq ft carpet with double-height sky terraces) — 32 layout variants across five towers.",
  },
  {
    q: "What is the difference between Smart, Comfort and Luxury 3 BHK layouts?",
    a: "Smart is the most compact and efficient, Comfort adds larger rooms, and Luxury offers the biggest carpet areas with 3 toilets and the largest sky terraces. Balcony, BUA and SBUA vary by tower, type and odd/even floor.",
  },
  {
    q: "What are the carpet areas of 3 BHK homes at Vinayak 21 Acres?",
    a: "Smart layouts are approximately 698–703 sq ft, Comfort approximately 736–783 sq ft, and Luxury approximately 823–943 sq ft carpet area. Exact figures vary by tower, type and floor — see each layout's card on this page.",
  },
  {
    q: "Are 3 BHK flats available near Newtown Action Area III?",
    a: "Yes. Vinayak 21 Acres at Hatishala — off and adjacent to Newtown Action Area III — offers all three 3 BHK families with official floor plans available to view on this page.",
  },
  {
    q: "What is the current 3 BHK price at Vinayak 21 Acres?",
    a: "Pricing and availability are subject to change. Contact Forest Hill Realtors for the latest developer-provided pricing and available inventory.",
  },
  {
    q: "Where can I see the 3 BHK floor plans?",
    a: "Every official 3 BHK drawing can be viewed from the layout cards on this page, and the complete collection is on our floor plans page.",
  },
  {
    q: "How can I book a site visit?",
    a: "Use any Book Site Visit button on this page, the sticky enquiry bar on mobile, or message us on WhatsApp — Forest Hill Realtors will arrange a convenient slot.",
  },
];

export default function ThreeBhk() {
  const [modal, setModal] = useState({ open: false, source: "3 BHK Page Enquiry", interest: "3 BHK" });
  const [planView, setPlanView] = useState(null);
  const [contact, setContact] = useState({ phone: "+919163841785", whatsapp: "+919163841785" });

  useEffect(() => {
    document.title = "3 BHK Flats in New Town Kolkata | New Town 3 BHK Property";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Looking for 3 BHK flats in New Town Kolkata? Explore Smart, Comfort and Luxury 3 BHK options at Vinayak 21 Acres near Newtown Action Area III."
      );
    initLenis();
    getSiteSettings().then(setContact);
  }, []);

  const openModal = (source, interest = "3 BHK") => setModal({ open: true, source, interest });
  const waLink = `https://wa.me/${digitsOnly(contact.whatsapp)}?text=${encodeURIComponent(WA_MESSAGE)}`;

  return (
    <div className="bg-[#FFFFFF]" data-testid="threebhk-page">
      <Header onBookVisit={() => openModal("Site Visit Booking")} phone={contact.phone} />
      <main>
        <section className="relative bg-[#EAF5D6] pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden hero-grain">
          <div className="absolute inset-0 opacity-[0.14]" aria-hidden="true">
            <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline-tag" data-testid="threebhk-overline">Buyer's Guide · Forest Hill Realtors</p>
            <h1
              className="font-serif-display text-[#3F6212] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6 max-w-4xl"
              data-testid="threebhk-h1"
            >
              3 BHK Flats in <span className="text-[#84CC16]">New Town Kolkata</span>
            </h1>
            <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg max-w-2xl leading-relaxed">
              A practical guide for buyers searching for a 3 BHK flat in New Town — how to compare layouts, what to
              verify, and a close look at the Smart, Comfort and Luxury 3 BHK options at Vinayak 21 Acres near
              Newtown Action Area 3.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/" data-testid="threebhk-home-link"
                className="inline-flex items-center gap-2 text-[#84CC16] font-sub font-semibold text-sm border border-[#84CC16]/50 rounded-full px-6 py-3 hover:bg-[#84CC16]/10 active:scale-[0.98] transition-all duration-300">
                Explore Vinayak 21 Acres <ArrowRight size={15} />
              </Link>
              <Link to="/2-bhk-flats-new-town-kolkata" data-testid="threebhk-2bhk-link"
                className="inline-flex items-center gap-2 text-[#3F6212] font-sub font-semibold text-sm border border-[#3F6212]/30 rounded-full px-6 py-3 hover:bg-[#3F6212]/10 active:scale-[0.98] transition-all duration-300">
                2 BHK Flats in New Town Kolkata <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </section>

        <section data-testid="threebhk-intro-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="01" eyebrow="Start Here" title="3 BHK Flats in New Town Kolkata" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  A 3 BHK gives a family room to grow — but across New Town, 3 BHK residential property varies
                  enormously in usable space, layout quality and daily livability. Headline sizes alone can mislead:
                  two homes with similar super built-up areas may offer very different carpet areas.
                </p>
                <p>
                  This guide explains what to compare when evaluating a 3 BHK property in New Town Kolkata, and
                  introduces one verified option near Action Area III with three distinct configuration families.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="threebhk-why-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="02" eyebrow="The Factors" title="Why Consider a 3 BHK in New Town?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {WHY_3BHK.map((f, i) => (
                <Reveal key={f.title} delay={0.04 * i}>
                  <div data-testid={`threebhk-factor-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl p-6 h-full hover:border-[#84CC16] hover:-translate-y-1 hover:shadow-[0_20px_40px_-18px_rgba(77,124,15,0.18)] transition-all duration-500">
                    <div className="w-11 h-11 rounded-full bg-[#EAF5D6] border border-[#84CC16]/30 flex items-center justify-center text-[#365314]">
                      <f.icon size={19} strokeWidth={1.6} />
                    </div>
                    <h3 className="font-serif-display text-xl text-[#365314] mt-4 leading-snug">{f.title}</h3>
                    <p className="text-sm text-[#65A30D] mt-2 leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="threebhk-checklist-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="03" eyebrow="Due Diligence" title="What to Check Before Buying a 3 BHK" />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  Start with <strong className="text-[#365314]">carpet area</strong> — the genuinely usable space —
                  and study the <strong className="text-[#365314]">floor plan</strong> for wasted passages. Check the{" "}
                  <strong className="text-[#365314]">number of toilets</strong> (2 versus 3 changes daily comfort
                  significantly), the <strong className="text-[#365314]">balcony or sky terrace</strong> provision,
                  and the <strong className="text-[#365314]">tower and floor</strong> of the specific unit.
                </p>
                <p>
                  Assess <strong className="text-[#365314]">ventilation</strong> and open views in person, review the{" "}
                  <strong className="text-[#365314]">amenities</strong> your family will use, ask for pricing with{" "}
                  <strong className="text-[#365314]">applicable additional charges</strong>, and confirm{" "}
                  <strong className="text-[#365314]">current inventory</strong> before shortlisting.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="threebhk-project-section" className="py-16 sm:py-24 bg-[#EAF5D6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="04" eyebrow="One Verified Option" title="3 BHK at Vinayak 21 Acres" dark />
            <Reveal delay={0.15}>
              <div className="mt-6 space-y-4 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                <p>
                  For buyers considering 3 BHK flats near Action Area III Kolkata, Vinayak 21 Acres is one residential
                  option to evaluate. Vinayak 21 Acres is located at Hatishala, off/adjacent to Newtown Action Area
                  III, Kolkata.
                </p>
                <p>
                  The project offers multiple 3 BHK categories — <strong className="text-[#3F6212]">3 BHK Smart</strong>,{" "}
                  <strong className="text-[#3F6212]">3 BHK Comfort</strong> and{" "}
                  <strong className="text-[#3F6212]">3 BHK Luxury</strong> — and these are not the same size: carpet
                  areas range from about 698 sq ft (Smart) to about 943 sq ft (Luxury). All sit within a development
                  featuring 90% homes with 3-side open views, double-height sky terraces, Vastu-compliant layouts, an
                  IGBC Precertified Platinum green rating and a 3-acre central park.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {CATEGORY_INFO.map((cat, ci) => (
          <section key={cat.key} data-testid={`threebhk-${cat.key}-section`}
            className={`py-16 sm:py-24 ${ci % 2 === 0 ? "" : "bg-[#F4FAE8]"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <SectionHeading index={`0${ci + 5}`} eyebrow={cat.title} title={cat.title} />
              <Reveal delay={0.1}>
                <p className="text-[#4D7C0F] text-sm sm:text-base leading-relaxed max-w-3xl mt-6">{cat.desc}</p>
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-10">
                {cat.plans.map((p, i) => (
                  <Reveal key={p.slug} delay={0.03 * (i % 4)}>
                    <div data-testid={`threebhk-plan-card-${p.slug}`}
                      className="bg-white border border-[#DCECC0] rounded-xl p-5 h-full flex flex-col hover:border-[#84CC16] transition-all duration-500">
                      <p className="font-mono-spec text-[10px] tracking-[0.15em] uppercase text-[#84CC16]">{p.tower}</p>
                      <h3 className="font-serif-display text-lg text-[#365314] mt-2">{p.config}</h3>
                      <div className="mt-3 space-y-1.5 text-[12px]">
                        {[["Carpet", p.carpet], ["Balcony", p.balcony], ["BUA", p.bua], ["SBUA", p.sbua]].map(([label, value]) => (
                          <div key={label} className="flex items-center justify-between">
                            <span className="text-[#65A30D]">{label}</span>
                            <span className="font-mono-spec text-[#365314]">{value}</span>
                          </div>
                        ))}
                      </div>
                      <button data-testid={`threebhk-view-${p.slug}`} onClick={() => setPlanView(p)}
                        className="mt-4 text-[12px] font-sub font-semibold border border-[#9CCB58] text-[#365314] rounded-full px-4 py-2.5 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                        View Floor Plan
                      </button>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        ))}

        <section data-testid="threebhk-floorplans-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <SectionHeading index="08" eyebrow="All Layouts" title="Vinayak 21 Acres 3 BHK Floor Plans" center />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                Browse every official 3 BHK drawing — Smart, Comfort and Luxury across all five towers — on our
                dedicated floor plans page.
              </p>
              <div className="mt-8">
                <Link to="/vinayak-21-acres-floor-plan" data-testid="threebhk-floorplan-link"
                  className="inline-flex items-center gap-2 bg-[#84CC16] text-[#365314] font-sub font-bold text-sm px-8 py-4 rounded-full hover:bg-[#9ADB3E] active:scale-[0.98] transition-all duration-300">
                  View 3 BHK Floor Plans <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="threebhk-price-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="09" eyebrow="Pricing" title="Vinayak 21 Acres 3 BHK Price" />
            <Reveal delay={0.15}>
              <div className="bg-white border border-[#DCECC0] rounded-2xl p-7 sm:p-10 mt-8">
                <p className="text-[#4D7C0F] text-base sm:text-lg leading-relaxed" data-testid="threebhk-price-note">
                  Pricing and availability are subject to change. Contact Forest Hill Realtors for the latest
                  developer-provided pricing and available inventory.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="threebhk-get-price-btn" onClick={() => openModal("Get Latest 3 BHK Price")}
                    className="bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-3.5 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    Get Latest 3 BHK Price
                  </button>
                  <button data-testid="threebhk-check-availability-btn" onClick={() => openModal("Check 3 BHK Availability")}
                    className="text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Check 3 BHK Availability
                  </button>
                  <Link to="/vinayak-21-acres-price" data-testid="threebhk-price-link"
                    className="inline-flex items-center gap-2 text-[#365314] font-sub font-semibold text-sm border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300">
                    Vinayak 21 Acres Price <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="threebhk-location-section" className="py-16 sm:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="10" eyebrow="Connectivity" title="Location & Connectivity" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                From the Hatishala vicinity, indicative travel times include Infosys about 1 minute, ITC Infotech and
                Wipro about 2 minutes, the nearest metro and Biswa Bangla Gate about 12 minutes, Tata Medical Centre
                about 12 minutes, St Xavier's University about 7 minutes, and the airport around 30 minutes. Travel
                times are indicative and may vary depending on traffic and route.
              </p>
            </Reveal>
          </div>
        </section>

        <section data-testid="threebhk-amenities-section" className="py-16 sm:py-24 bg-[#F4FAE8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="11" eyebrow="Lifestyle" title="Amenities & Lifestyle" />
            <Reveal delay={0.15}>
              <p className="mt-6 text-[#4D7C0F] text-base sm:text-lg leading-relaxed">
                The development centres on a 3-acre central park with elevated cabanas, a grand lawn, kids' and
                adventure play areas, an open-air amphitheatre and a maze garden, plus an athletic track, futsal,
                lawn tennis, cricket, pickleball, badminton and mini basketball, a pet park, forest trail, jogging and
                cycling tracks, senior citizens' seating and an outdoor fitness zone — with a planned shuttle service
                to the nearest metro.
              </p>
            </Reveal>
          </div>
        </section>

        <section data-testid="threebhk-visit-section" className="pb-16 sm:pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl bg-[#EAF5D6] p-8 sm:p-12 text-center hero-grain">
                <p className="overline-tag">12 · Visit</p>
                <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3F6212] leading-tight mt-4">
                  Book a Site Visit
                </h2>
                <p className="text-[#4D7C0F] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                  Compare Smart, Comfort and Luxury layouts in person — and see which 3 BHK truly fits your family.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
                  <button data-testid="threebhk-book-visit-btn" onClick={() => openModal("Site Visit Booking")}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-7 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                    <CalendarCheck size={16} /> Book a Site Visit
                  </button>
                  <a data-testid="threebhk-whatsapp-btn" href={waLink} target="_blank" rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-sub font-bold text-sm px-7 py-4 rounded-full hover:bg-[#1EBE57] active:scale-[0.98] transition-all duration-300">
                    <MessageSquare size={16} /> WhatsApp Us
                  </a>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2">
                  <Link to="/best-property-in-new-town-kolkata" data-testid="threebhk-guide-link"
                    className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Best Property in New Town Kolkata Guide <ArrowRight size={14} />
                  </Link>
                  <Link to="/properties-in-new-town-action-area-3" data-testid="threebhk-aa3-link"
                    className="inline-flex items-center gap-2 text-[#84CC16] text-sm font-sub font-medium hover:underline">
                    Properties in New Town Action Area 3 <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="threebhk-faq-section" className="pb-16 sm:pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading index="13" eyebrow="FAQs" title="3 BHK Buyer FAQs" center />
            <Reveal delay={0.15}>
              <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="threebhk-faq-accordion">
                {THREE_BHK_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`threebhk-faq-item-${i}`}
                    className="bg-white border border-[#DCECC0] rounded-xl px-6 data-[state=open]:border-[#84CC16] transition-colors duration-300">
                    <AccordionTrigger data-testid={`threebhk-faq-trigger-${i}`}
                      className="text-left font-sub font-medium text-[15px] text-[#365314] hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-sm text-[#4D7C0F] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        <div className="bg-[#EAF5D6] border-t border-[#DCECC0] py-7" data-testid="threebhk-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <ShieldCheck size={17} className="text-[#84CC16] shrink-0" />
            <p className="text-[#4D7C0F] text-xs sm:text-[13px] leading-relaxed">
              <span className="text-[#3F6212] font-sub font-semibold">Forest Hill Realtors</span> · Authorised Channel
              Partner · WBRERA Agent Registration: <span className="font-mono-spec">{AGENT_REG}</span>
            </p>
          </div>
        </div>

        <Footer />
      </main>

      <AnimatePresence>
        {planView && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#F4FAE8]/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-8"
            onClick={() => setPlanView(null)} data-testid="threebhk-plan-viewer-overlay">
            <motion.div initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[92svh] overflow-y-auto p-4 sm:p-6"
              data-testid="threebhk-plan-viewer-modal">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="overline-tag">{planView.tower}</p>
                  <h3 className="font-serif-display text-2xl text-[#365314] mt-2">{planView.config}</h3>
                </div>
                <button data-testid="threebhk-plan-viewer-close-btn" onClick={() => setPlanView(null)}
                  className="p-2 text-[#65A30D] hover:text-[#365314] transition-colors" aria-label="Close">
                  <X size={22} />
                </button>
              </div>
              <img src={planView.planImage} alt={`Official floor plan — ${planView.config}, ${planView.tower}, Vinayak 21 Acres`}
                className="w-full h-auto rounded-lg border border-[#DCECC0]" />
              <p className="text-[11px] text-[#65A30D] mt-3 leading-relaxed">
                {planView.note} Plan from the official project brochure; subject to change — verify with Forest Hill Realtors.
              </p>
              <button data-testid="threebhk-plan-viewer-enquire-btn"
                onClick={() => { const s = `Floor Plan Enquiry: ${planView.config} · ${planView.tower}`; setPlanView(null); openModal(s); }}
                className="mt-5 w-full bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-6 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300">
                Enquire About This Plan
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <LeadModal open={modal.open} onClose={() => setModal((m) => ({ ...m, open: false }))} source={modal.source} defaultInterest={modal.interest} whatsapp={contact.whatsapp} />
      <StickyMobileBar phone={contact.phone} whatsapp={contact.whatsapp} onEnquire={() => openModal("Quick Enquiry")} />
      <WhatsAppFloat whatsapp={contact.whatsapp} />
    </div>
  );
}
